import { Alert, Tooltip } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Icon } from '@festina-life/ui/structure'
import cx from 'classnames'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'

import { useScenarioStatusContext } from '../../context/scenarioStatusContext'
import { ErrorMessageBody } from '../errorFallback/ErrorMessageBody'
import { scenarioStyles } from '../participantOverview/scenarios/scenarioStyles'
import type { RankedItem } from '../search/commandPalette/types'
import { checkApiDataResponse } from '../search/commandPalette/utils'
import { NavItem } from './NavItem'

const alertBaseProps = {
  variant: 'mini' as const,
  className: cx('mr-1'),
}

type Props = {
  label: ReactNode
  items?: RankedItem[]
  children?: ReactNode
  isTriggerClickSupported?: boolean
}

export const NavMenu = ({
  label,
  items,
  isTriggerClickSupported = true,
  children,
}: Props) => {
  const translate = useTranslate()
  const [isNavigatingToGroup, setIsNavigatingToGroup] = useState(false)
  const { pathname } = useLocation()
  const { isScenarioActive } = useScenarioStatusContext()

  const fakeInstantNavigation = () => {
    // This is to give instant feedback to the user that navigation is (going to) happen
    setIsNavigatingToGroup(true)
  }

  useEffect(() => {
    setIsNavigatingToGroup(false)
  }, [pathname])

  const firstAvailableItemAction = Object.values(items ?? {}).find((x) =>
    checkApiDataResponse(x.apiResponse),
  )
  const hasOptionsWithData = children || !!firstAvailableItemAction
  const location = useLocation()
  const isActiveGroup = useMemo(
    () =>
      !!Object.values(items ?? {}).some(
        ({ action }) =>
          typeof action === 'string' && location.pathname === action,
      ),
    [items, location.pathname],
  )
  const hasAnyItemActiveScenario = useMemo(
    () =>
      isScenarioActive &&
      !!Object.values(items ?? {}).some(
        ({ supportsScenarios }) => supportsScenarios,
      ),
    [items, isScenarioActive],
  )

  return (
    <Tooltip
      // TODO: change to use the dropdown component once it supports hover too. And most like the tooltip wouldn't need hoverDelay and offset props then https://festinafinance.atlassian.net/browse/LIFE-7654
      className="group/navmenu"
      tipClassName="flex flex-col rounded-t-none"
      isInteractive
      hoverDelay={0}
      offset={0}
      hasMinWidthOfAnchorWidth
      shouldRenderInPortal={false}
      tip={
        children ||
        Object.values(items ?? {}).map(
          ({
            title,
            action,
            presentation,
            apiResponse,
            isSelected,
            supportsScenarios,
            // We don't need to factor in hasRequiredComponents as useSearch already filters those out
          }) => {
            const hasData = checkApiDataResponse(apiResponse)
            const applyScenarioStyling = isScenarioActive && supportsScenarios

            return (
              <NavItem
                key={title}
                to={typeof action === 'string' && hasData ? action : undefined}
                onClick={async () => {
                  if (hasData) {
                    fakeInstantNavigation()
                    if (typeof action === 'function') {
                      await action()
                    }
                  }
                }}
                style={applyScenarioStyling ? scenarioStyles : undefined}
                buttonVariant="discreet"
                className="justify-start text-base aria-[current=page]:bg-primary aria-[current=page]:text-primary-50"
                isDisabled={!hasData}
                title={
                  hasData || apiResponse?.error
                    ? undefined
                    : translate('option.noDataAvailable')
                }
              >
                {(apiResponse?.error || apiResponse?.isLoading) && (
                  <Alert
                    {...alertBaseProps}
                    severity="error"
                    variant="mini"
                    isLoading={apiResponse?.isLoading}
                  >
                    <ErrorMessageBody error={apiResponse?.error ?? null} />
                  </Alert>
                )}
                {presentation ?? title}
                {isSelected && (
                  <span className="ps-2 ms-auto">
                    <Icon name="Check" color="unset" />
                  </span>
                )}
              </NavItem>
            )
          },
        )
      }
    >
      <div className="relative mt-0.5">
        <NavItem
          to={
            isTriggerClickSupported &&
            typeof firstAvailableItemAction?.action === 'string' &&
            hasOptionsWithData
              ? firstAvailableItemAction.action
              : undefined
          }
          onClick={
            isTriggerClickSupported
              ? async () => {
                  if (hasOptionsWithData) {
                    fakeInstantNavigation()
                    if (
                      typeof firstAvailableItemAction?.action === 'function'
                    ) {
                      await firstAvailableItemAction.action()
                    }
                  }
                }
              : undefined
          }
          title={
            hasOptionsWithData ? undefined : translate('option.noDataAvailable')
          }
          isDisabled={!hasOptionsWithData}
          buttonVariant="discreet"
          style={hasAnyItemActiveScenario ? scenarioStyles : undefined}
          className={cx(
            'rounded-b-none border-b-2 border-transparent transition',
            (isNavigatingToGroup || isActiveGroup) && 'border-b-primary',
            'group-data-[amaze-isopen="true"]/navmenu:border-b-primary group-data-[amaze-isopen="true"]/navmenu:bg-current/10',
            'focus-visible:ring-0! focus-visible:ring-offset-0! focus-visible:inset-ring-0!',
            !isTriggerClickSupported && 'cursor-default!',
          )}
        >
          {label}
        </NavItem>
      </div>
    </Tooltip>
  )
}
